/**
 * NL-014 deterministic decimal contract.
 *
 * Values are supplied as decimal strings so calculation boundaries never use
 * JavaScript floating-point arithmetic. The caller owns the unit and decides
 * which declared output scale is appropriate; this module has no pricing,
 * source, receipt, provider, or runtime integration knowledge.
 */
export const DECIMAL_ROUNDING_CONTRACT_VERSION = "nl014-decimal-v1";

export const DECIMAL_ROUNDING_MODE = "half-up" as const;

/** Keeps serialized output bounded while still covering normal quote units. */
export const MAX_DECIMAL_OUTPUT_SCALE = 18;

export type DecimalQuantity = {
  readonly value: string;
  readonly unit: string;
};

export type DecimalRoundingInput = {
  readonly quantity: DecimalQuantity;
  readonly outputScale: number;
};

export type DecimalRoundingBlocker =
  | "missing-quantity"
  | "invalid-decimal-value"
  | "missing-unit"
  | "invalid-output-scale";

export type DecimalRoundingResult = {
  readonly contractVersion: typeof DECIMAL_ROUNDING_CONTRACT_VERSION;
  readonly roundingMode: typeof DECIMAL_ROUNDING_MODE;
  readonly readiness: "ready" | "blocked";
  readonly blockers: readonly DecimalRoundingBlocker[];
  readonly roundedQuantity: DecimalQuantity | null;
  readonly outputScale: number | null;
};

type DecimalParts = {
  readonly negative: boolean;
  readonly whole: string;
  readonly fraction: string;
};

type UnknownRecord = Readonly<Record<string, unknown>>;

const DECIMAL_VALUE = /^([+-]?)(\d+)(?:\.(\d+))?$/;

const NEXT_DIGIT: Readonly<Record<string, string>> = {
  "0": "1",
  "1": "2",
  "2": "3",
  "3": "4",
  "4": "5",
  "5": "6",
  "6": "7",
  "7": "8",
  "8": "9",
};

function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function parseDecimal(value: string | null): DecimalParts | null {
  if (!value) return null;

  const match = DECIMAL_VALUE.exec(value);
  if (!match) return null;

  return {
    negative: match[1] === "-",
    whole: match[2],
    fraction: match[3] ?? "",
  };
}

function isValidOutputScale(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= MAX_DECIMAL_OUTPUT_SCALE
  );
}

function incrementDigits(value: string): string {
  const digits = value.split("");

  for (let index = digits.length - 1; index >= 0; index -= 1) {
    if (digits[index] === "9") {
      digits[index] = "0";
      continue;
    }

    digits[index] = NEXT_DIGIT[digits[index]];
    return digits.join("");
  }

  return `1${digits.join("")}`;
}

function normalizeWhole(value: string): string {
  return value.replace(/^0+(?=\d)/, "");
}

function hasNonZeroDigit(value: string): boolean {
  return /[1-9]/.test(value);
}

function formatRoundedDecimal(negative: boolean, digits: string, outputScale: number): string {
  const whole = outputScale === 0 ? digits : digits.slice(0, -outputScale);
  const fraction = outputScale === 0 ? "" : digits.slice(-outputScale);
  const normalizedWhole = normalizeWhole(whole);
  const sign = negative && hasNonZeroDigit(`${normalizedWhole}${fraction}`) ? "-" : "";

  return outputScale === 0 ? `${sign}${normalizedWhole}` : `${sign}${normalizedWhole}.${fraction}`;
}

function roundParsedDecimalHalfUp(value: DecimalParts, outputScale: number): string {
  const retainedFraction = value.fraction.padEnd(outputScale, "0").slice(0, outputScale);
  const discardedFirstDigit = value.fraction.charAt(outputScale);
  const absoluteDigits = `${value.whole}${retainedFraction}`;
  const roundedDigits = discardedFirstDigit >= "5" ? incrementDigits(absoluteDigits) : absoluteDigits;

  return formatRoundedDecimal(value.negative, roundedDigits, outputScale);
}

/**
 * Rounds a caller-supplied decimal quantity at its declared output boundary.
 * Exact halves round away from zero, invalid serialized input remains blocked,
 * and the input object is never mutated.
 */
export function roundDecimalQuantityHalfUp(input: unknown): DecimalRoundingResult {
  const blockers: DecimalRoundingBlocker[] = [];
  const candidate = asRecord(input);
  const quantity = asRecord(candidate?.quantity);

  if (!quantity) blockers.push("missing-quantity");

  const decimal = parseDecimal(nonEmptyString(quantity?.value));
  const unit = nonEmptyString(quantity?.unit);
  const outputScale = candidate?.outputScale;

  if (!decimal) blockers.push("invalid-decimal-value");
  if (!unit) blockers.push("missing-unit");
  if (!isValidOutputScale(outputScale)) blockers.push("invalid-output-scale");

  if (!decimal || !unit || !isValidOutputScale(outputScale)) {
    return {
      contractVersion: DECIMAL_ROUNDING_CONTRACT_VERSION,
      roundingMode: DECIMAL_ROUNDING_MODE,
      readiness: "blocked",
      blockers,
      roundedQuantity: null,
      outputScale: null,
    };
  }

  return {
    contractVersion: DECIMAL_ROUNDING_CONTRACT_VERSION,
    roundingMode: DECIMAL_ROUNDING_MODE,
    readiness: "ready",
    blockers,
    roundedQuantity: {
      value: roundParsedDecimalHalfUp(decimal, outputScale),
      unit,
    },
    outputScale,
  };
}
